import { useQuery } from "@tanstack/react-query";
import { useEffect, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import type { AuthUser } from "../app/auth";
import { api, type DashboardSummary } from "../app/api";
import { loadUiSettings } from "../app/ui-settings";
import { isActionMenuEventTarget, useActionMenuAutoClose, useFloatingActionMenu } from "../hooks/useActionMenuAutoClose";

type SummaryResolution = DashboardSummary["currentResolutions"][number];
type QualityItem = DashboardSummary["dataQuality"][number];
type DashboardPageProps = {
  authUser: AuthUser;
};

const numberFormat = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });
const percentFormat = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 });

const formatDate = (value?: string) => {
  if (!value) return "-";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value;
  return `${match[3]}.${match[2]}.${match[1]}`;
};

const statusLabel = (status: SummaryResolution["status"]) => {
  if (status === "aktiv") return "Laufend";
  if (status === "entwurf") return "Entwurf";
  return "Abgeschlossen";
};

const qualityToneLabel = (tone: QualityItem["tone"]) => {
  if (tone === "critical") return "Kritisch";
  if (tone === "warning") return "Hinweis";
  return "OK";
};

const qualityTargetHref = (item: QualityItem) => {
  if (item.key === "units_without_assignment") return "/debug#assignment-anomalies-without";
  if (item.key === "multiple_assignments") return "/debug#assignment-anomalies-multiple";
  return item.href;
};

type DashboardIconName =
  | "current"
  | "return"
  | "deadline"
  | "quality"
  | "chart"
  | "check"
  | "arrow"
  | "vote"
  | "outcome"
  | "quorum"
  | "list"
  | "object"
  | "status"
  | "calendar"
  | "trend"
  | "action";

const DashboardIcon = ({ name }: { name: DashboardIconName }) => (
  <span className="dashboard-v2-icon" aria-hidden="true">
    {name === "current" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M7.1 4.6h6.6l3.2 3.2v11.6H7.1z" stroke="currentColor" strokeWidth="1.85" strokeLinejoin="round" />
        <path d="M13.7 4.8v3.1h3" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m9.4 14.1 2 2 4.2-5" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="17.7" cy="17.8" r="2.1" fill="#fff" stroke="currentColor" strokeWidth="1.85" />
      </svg>
    ) : null}
    {name === "return" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M18.2 8.7a7 7 0 1 0 .5 8.2" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        <path d="m16.7 5.8 2.8 3-3 2.7" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
    {name === "deadline" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="7.4" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 8v4.2l2.8 1.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
    {name === "quality" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M12 4.5 19 8v5.2c0 3.4-2.8 5.6-7 6.8-4.2-1.2-7-3.4-7-6.8V8l7-3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="m9 12.4 2 2 4.1-4.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
    {name === "chart" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M5.5 18.5h13M7.2 18.5v-6.8M12 18.5V7M16.8 18.5v-9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ) : null}
    {name === "check" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="m6.4 12.6 3.5 3.5 7.8-8.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
    {name === "arrow" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M8 5.5 14.5 12 8 18.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
    {name === "vote" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M6.3 12.6 10 16.2 17.8 7.8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ) : null}
    {name === "outcome" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M8 20h8M12 16v4M7 5h10v4.8a5 5 0 0 1-10 0V5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 7H4.8v2.2A3.8 3.8 0 0 0 7 12.6M17 7h2.2v2.2a3.8 3.8 0 0 1-2.2 3.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
    {name === "quorum" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <circle cx="8" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="16" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.8" />
        <path d="M4.8 18c.7-2.6 2.5-4 5.2-4M14 14c2.7 0 4.5 1.4 5.2 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ) : null}
    {name === "list" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M8 7h10M8 12h10M8 17h10" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        <path d="M5 7h.01M5 12h.01M5 17h.01" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
      </svg>
    ) : null}
    {name === "object" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M5.5 19V6.5h8V19M13.5 10h5V19" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M8.2 9h2.5M8.2 12h2.5M8.2 15h2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ) : null}
    {name === "status" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M7 12h10M12 7v10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    ) : null}
    {name === "calendar" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M6 8h12M8 5v3M16 5v3M6 6.5h12v12H6z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
    {name === "trend" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M5 16.5 9 12l3.3 3.1L18.8 7.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15 7.5h3.8v3.8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
    {name === "action" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M9 5h10v10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.5 5.5 8 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M6 8v11h11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
  </span>
);

const MetricLabel = ({
  label
}: {
  label: string;
}) => (
  <span className="dashboard-v2-metric-label">{label}</span>
);

const PanelTitle = ({ eyebrow, title, icon }: { eyebrow: string; title: string; icon: DashboardIconName }) => (
  <div className="dashboard-v2-panel-title">
    <DashboardIcon name={icon} />
    <div>
      <span className="dashboard-v2-eyebrow">{eyebrow}</span>
      <h3>{title}</h3>
    </div>
  </div>
);

const DashboardTableIcon = ({ name }: { name: "resolution" | "object" | "status" | "calendar" | "return" | "quorum" | "actions" }) => (
  <>
    {name === "resolution" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M7.1 4.6h6.6l3.2 3.2v11.6H7.1z" stroke="currentColor" strokeWidth="1.85" strokeLinejoin="round" />
        <path d="M13.7 4.8v3.1h3" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m9.4 14.1 2 2 4.2-5" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="17.7" cy="17.8" r="2.1" fill="#fff" stroke="currentColor" strokeWidth="1.85" />
      </svg>
    ) : null}
    {name === "object" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M4.9 8.2h14.2v10.9H4.9z" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8.1 4.9h7.8v3.3H8.1zM8.2 12h2.6M13.2 12h2.6M8.2 15.4h2.6M13.2 15.4h2.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ) : null}
    {name === "status" ? (
      <svg viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="5.9" stroke="currentColor" strokeWidth="1.7" />
        <path d="m10 7.2 1.8 2.5L10 12.8 8.2 9.7 10 7.2Z" fill="currentColor" />
      </svg>
    ) : null}
    {name === "calendar" ? (
      <svg viewBox="0 0 20 20" fill="none">
        <path d="M4.6 7.1h10.8M6.5 4.4v2.7M13.5 4.4v2.7M4.6 5.7h10.8v10H4.6z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
    {name === "return" ? (
      <svg viewBox="0 0 20 20" fill="none">
        <path d="M15.2 7.2a5.8 5.8 0 1 0 .4 6.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <path d="m14 4.7 2.3 2.5-2.5 2.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
    {name === "quorum" ? (
      <svg viewBox="0 0 20 20" fill="none">
        <circle cx="6.9" cy="7.6" r="2.1" stroke="currentColor" strokeWidth="1.7" />
        <circle cx="13.1" cy="7.6" r="2.1" stroke="currentColor" strokeWidth="1.7" />
        <path d="M3.8 15.3c.6-2.2 2.2-3.4 4.4-3.4M11.8 11.9c2.2 0 3.8 1.2 4.4 3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    ) : null}
    {name === "actions" ? (
      <svg viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="5.3" r="1.4" fill="currentColor" />
        <circle cx="10" cy="10" r="1.4" fill="currentColor" />
        <circle cx="10" cy="14.7" r="1.4" fill="currentColor" />
      </svg>
    ) : null}
  </>
);

const TableHeadLabel = ({ icon, children }: { icon: "resolution" | "object" | "status" | "calendar" | "return" | "quorum" | "actions"; children?: ReactNode }) => (
  <span className="resolutions-v2-th-plain">
    <span className="resolutions-v2-th-icon" aria-hidden="true"><DashboardTableIcon name={icon} /></span>
    {children ? <span className="resolutions-v2-th-label">{children}</span> : null}
  </span>
);

const QualitySymbol = ({ tone }: { tone: QualityItem["tone"] }) => (
  <span className={`dashboard-v2-quality-symbol dashboard-v2-quality-symbol--${tone}`} aria-hidden="true">
    {tone === "critical" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M12 7.2v6.1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M12 17h.01" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
      </svg>
    ) : tone === "warning" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M12 10.7v5.7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M12 7.4h.01" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
      </svg>
    ) : (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="m6.8 12.7 3.1 3.1 7.2-7.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </span>
);

const KpiCard = ({
  icon,
  label,
  value,
  note,
  tone = "neutral"
}: {
  icon: "current" | "return" | "deadline" | "quality";
  label: string;
  value: string;
  note: ReactNode;
  tone?: "neutral" | "ok" | "warn" | "critical";
}) => (
  <div className={`dashboard-v2-kpi-card dashboard-v2-kpi-card--${tone}`}>
    <DashboardIcon name={icon} />
    <div className="dashboard-v2-kpi-copy">
      <span>{label}</span>
      <strong>{value}</strong>
      <div className="dashboard-v2-kpi-note">{note}</div>
    </div>
  </div>
);

const PercentBar = ({ items }: { items: Array<{ label: string; value: number; className: string }> }) => (
  <div className="dashboard-v2-segment-wrap">
    <div className="dashboard-v2-segment-track" aria-hidden="true">
      {items.map((item) => (
        <span key={item.label} className={item.className} style={{ width: `${Math.max(0, Math.min(100, item.value))}%` }} />
      ))}
    </div>
    <div className="dashboard-v2-segment-legend">
      {items.map((item) => (
        <span key={item.label}>
          <i className={item.className} aria-hidden="true" />
          {item.label}: <strong>{percentFormat.format(item.value)} %</strong>
        </span>
      ))}
    </div>
  </div>
);

const ProgressBar = ({ value }: { value: number }) => {
  const progress = Math.max(0, Math.min(100, value));
  return (
    <div className="dashboard-v2-progress">
      <span style={{ width: `${progress}%` }} />
    </div>
  );
};

const DeadlineLabel = ({ resolution }: { resolution: SummaryResolution }) => {
  if (resolution.status === "entwurf") return <span className="dashboard-v2-deadline-neutral">Noch nicht gestartet</span>;
  if (resolution.isCriticalAfterSecondReminder) return <span className="dashboard-v2-deadline-critical">Nach 2. Erinnerung</span>;
  if (!resolution.endedatum) return null;
  if (resolution.daysUntilEnd === null) return <span className="dashboard-v2-deadline-neutral">{formatDate(resolution.endedatum)}</span>;
  if (resolution.daysUntilEnd <= 3) return <span className="dashboard-v2-deadline-warning">{resolution.daysUntilEnd} Tage</span>;
  return <span className="dashboard-v2-deadline-neutral">{formatDate(resolution.endedatum)}</span>;
};

export const DashboardPage = ({ authUser }: DashboardPageProps) => {
  const navigate = useNavigate();
  const { data: summary, isLoading, isError } = useQuery({
    queryKey: ["dashboard-summary-v2"],
    queryFn: api.dashboardSummary
  });
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [isActionMenuHovered, setIsActionMenuHovered] = useState(false);
  const [qualityPermissionNotice, setQualityPermissionNotice] = useState("");
  const [isCurrentResolutionsModalOpen, setIsCurrentResolutionsModalOpen] = useState(false);
  const { actionMenuPosition, openActionMenuAtTrigger } = useFloatingActionMenu({
    openMenuId: openActionMenuId,
    setOpenMenuId: setOpenActionMenuId
  });

  useEffect(() => {
    if (!openActionMenuId) return;
    const handleMouseDown = (event: MouseEvent) => {
      if (!isActionMenuEventTarget(event.target)) setOpenActionMenuId(null);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenActionMenuId(null);
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openActionMenuId]);

  useActionMenuAutoClose({
    isOpen: !!openActionMenuId,
    isHovered: isActionMenuHovered,
    closeMenu: () => setOpenActionMenuId(null)
  });

  const isFrameworkV2 =
    loadUiSettings().management.uiFramework === "mockup_v2" ||
    (typeof document !== "undefined" && document.body.classList.contains("framework-v2"));

  if (!summary && isLoading) {
    return null;
  }

  if (!summary || isError) {
    return (
      <section className="panel dashboard-v2-loading">
        <span>Dashboard-Daten konnten nicht geladen werden.</span>
      </section>
    );
  }

  const voteItems = [
    { label: "Ja", value: summary.historicalMetrics.voteDistribution.ja, className: "dashboard-v2-segment--yes" },
    { label: "Nein", value: summary.historicalMetrics.voteDistribution.nein, className: "dashboard-v2-segment--no" },
    { label: "Enthaltung", value: summary.historicalMetrics.voteDistribution.enthaltung, className: "dashboard-v2-segment--abstain" }
  ];
  const completedOutcomeItems = [
    { label: "Angenommen", value: summary.historicalMetrics.completedOutcomes.angenommen, className: "dashboard-v2-segment--yes" },
    { label: "Abgelehnt", value: summary.historicalMetrics.completedOutcomes.abgelehnt, className: "dashboard-v2-segment--no" }
  ];
  const quorumItems = [
    { label: "Quorum erreicht", value: summary.historicalMetrics.quorumDistribution.erreicht, className: "dashboard-v2-segment--active" },
    { label: "Nicht erreicht", value: summary.historicalMetrics.quorumDistribution.nichtErreicht, className: "dashboard-v2-segment--no" }
  ];
  const criticalLabel = `${summary.kpis.criticalDeadlines} kritisch`;
  const displayedCurrentResolutions = summary.currentResolutions.slice(0, 2);
  const hasMoreCurrentResolutions = summary.currentResolutions.length > 2;
  const canOpenDebugQualityDetails = authUser.permissions.debug !== false;
  const handleQualityClick = (event: ReactMouseEvent<HTMLAnchorElement>, item: QualityItem) => {
    const needsDebugAccess = item.key === "units_without_assignment" || item.key === "multiple_assignments";
    if (!needsDebugAccess) return;
    if (canOpenDebugQualityDetails) return;
    event.preventDefault();
    setQualityPermissionNotice("Bitte wenden Sie sich an den Administrator.");
  };

  const renderCurrentResolutionRows = (rows: SummaryResolution[], context: "dashboard" | "modal") =>
    rows.map((resolution) => {
      const menuId = `${context}:${resolution.id}`;
      return (
        <tr key={resolution.id} className={resolution.isCriticalAfterSecondReminder ? "is-critical" : ""}>
          <td>
            <div className="dashboard-v2-resolution-cell">
              <strong>{resolution.beschlussnummer}</strong>
              <span className="dashboard-v2-cell-separator">|</span>
              <span>{resolution.titel}</span>
            </div>
          </td>
          <td>
            <div className="dashboard-v2-object-cell">
              <strong>{resolution.objektnummer}</strong>
              <span className="dashboard-v2-cell-separator">|</span>
              <span>{resolution.objektname || "-"}</span>
            </div>
          </td>
          <td className="dashboard-v2-current-table-center">
            <div className="dashboard-v2-center-body-cell">
              <span className={`badge dashboard-v2-status-badge ${resolution.status === "aktiv" ? "aktiv" : "entwurf"}`}>{statusLabel(resolution.status)}</span>
            </div>
          </td>
          <td className="dashboard-v2-current-table-center">
            <div className="dashboard-v2-center-body-cell">
              <DeadlineLabel resolution={resolution} />
            </div>
          </td>
          <td className="dashboard-v2-current-table-center">
            <div className="dashboard-v2-return-cell">
              <span>{percentFormat.format(resolution.ruecklaufquote)} %</span>
              <ProgressBar value={resolution.ruecklaufquote} />
            </div>
          </td>
          <td>
            <div className="resolutions-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="resolutions-v2-action-trigger"
                aria-label={`Aktionen für ${resolution.beschlussnummer}`}
                aria-expanded={openActionMenuId === menuId}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  if (openActionMenuId === menuId) {
                    setOpenActionMenuId(null);
                    return;
                  }
                  openActionMenuAtTrigger(menuId, e.currentTarget, 52);
                }}
              >
                ⋮
              </button>
              {openActionMenuId === menuId && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                <div
                  className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed"
                  style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseEnter={() => setIsActionMenuHovered(true)}
                  onMouseLeave={() => setIsActionMenuHovered(false)}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setOpenActionMenuId(null);
                      setIsCurrentResolutionsModalOpen(false);
                      navigate(`/resolutions/${resolution.id}`);
                    }}
                  >
                    Öffnen
                  </button>
                </div>,
                document.body
              ) : null}
            </div>
          </td>
        </tr>
      );
    });

  const renderCurrentResolutionsTable = (rows: SummaryResolution[], context: "dashboard" | "modal") => (
    <div className="dashboard-v2-table-wrap">
      <table className="dashboard-v2-current-table">
        <colgroup>
          <col className="dashboard-v2-current-col-resolution" />
          <col className="dashboard-v2-current-col-object" />
          <col className="dashboard-v2-current-col-status" />
          <col className="dashboard-v2-current-col-deadline" />
          <col className="dashboard-v2-current-col-return" />
          <col className="dashboard-v2-current-col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th><TableHeadLabel icon="resolution">Beschluss</TableHeadLabel></th>
            <th><TableHeadLabel icon="object">Objekt</TableHeadLabel></th>
            <th className="dashboard-v2-current-table-center"><div className="dashboard-v2-center-head-cell"><TableHeadLabel icon="status">Status</TableHeadLabel></div></th>
            <th className="dashboard-v2-current-table-center"><div className="dashboard-v2-center-head-cell"><TableHeadLabel icon="calendar">Frist</TableHeadLabel></div></th>
            <th className="dashboard-v2-current-table-center"><TableHeadLabel icon="return">Rücklaufquote</TableHeadLabel></th>
            <th className="resolutions-v2-actions-head" aria-label="Aktionen" title="Aktionen"><TableHeadLabel icon="actions" /></th>
          </tr>
        </thead>
        <tbody>{renderCurrentResolutionRows(rows, context)}</tbody>
      </table>
    </div>
  );

  if (!isFrameworkV2) {
    return (
      <>
        <section className="page-header panel">
          <div>
            <h2 style={{ margin: 0 }}>Dashboard</h2>
            <small style={{ color: "#5a6875" }}>Beschlusslage und Datenqualität</small>
          </div>
        </section>
        <section className="kpi-grid">
          <div className="kpi">
            Aktuelle Beschlüsse
            <strong>{summary.kpis.currentResolutions}</strong>
          </div>
          <div className="kpi">
            Rücklauf gesamt
            <strong>{percentFormat.format(summary.kpis.totalReturnRate)} %</strong>
          </div>
          <div className="kpi">
            Fristen im Blick
            <strong>{criticalLabel}</strong>
          </div>
          <div className="kpi">
            Datenqualität
            <strong>{summary.kpis.dataQualityIssues}</strong>
          </div>
        </section>
      </>
    );
  }

  return (
    <div className="dashboard-v2-page dashboard-v2-redesign">
      <section className="page-header panel resolutions-v2-page-header dashboard-v2-redesign-header">
        <h2 className="resolutions-v2-title" style={{ margin: 0 }}>
          <span className="resolutions-v2-title-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M4.7 10.1 12 4.6l7.3 5.5v8.8h-5.1v-5h-4.4v5H4.7v-8.8Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M8 19h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </span>
          <span className="resolutions-v2-title-copy">
            <span className="resolutions-v2-title-main">Dashboard</span>
            <span className="resolutions-v2-title-sub">Beschlusslage und Datenqualität</span>
          </span>
        </h2>
      </section>

      <section className="dashboard-v2-overview-grid">
        <div className="dashboard-v2-kpi-grid-redesign">
          <KpiCard
            icon="current"
            label="Aktuelle Beschlüsse"
            value={numberFormat.format(summary.kpis.currentResolutions)}
            note={
              <span className="dashboard-v2-kpi-badge-row">
                <span className="dashboard-v2-kpi-note-badge dashboard-v2-kpi-note-badge--active">{numberFormat.format(summary.kpis.activeResolutions)} laufend</span>
                <span className="dashboard-v2-kpi-note-badge dashboard-v2-kpi-note-badge--draft">{numberFormat.format(summary.kpis.draftResolutions)} Entwürfe</span>
              </span>
            }
          />
          <KpiCard icon="return" label="Rücklauf gesamt" value={`${percentFormat.format(summary.kpis.totalReturnRate)} %`} note="Durchschnitt aller Beschlüsse" />
          <KpiCard icon="deadline" label="Fristen im Blick" value={criticalLabel} note={`Nach 2. Erinnerung ohne Quorum`} tone={summary.kpis.criticalDeadlines > 0 ? "critical" : "ok"} />
          <KpiCard icon="quality" label="Datenqualität" value={`${numberFormat.format(summary.kpis.dataQualityIssues)} Hinweise`} note="Vor Beschlussstart prüfen" tone={summary.kpis.dataQualityIssues > 0 ? "warn" : "ok"} />
        </div>

        <div className="panel dashboard-v2-analytics-panel">
          <div className="dashboard-v2-panel-head">
            <PanelTitle eyebrow="Vergangenheit" title="Beschlusslage" icon="current" />
          </div>
          <div className="dashboard-v2-chart-stack">
            <div>
              <div className="dashboard-v2-chart-title">
                <MetricLabel label="Stimmenverteilung aller Beschlüsse" />
                <strong>{percentFormat.format(summary.historicalMetrics.averageReturnRate)} % Ø Rücklauf</strong>
              </div>
              <PercentBar items={voteItems} />
            </div>
            <div>
              <div className="dashboard-v2-chart-title">
                <MetricLabel label="Abgeschlossene Beschlüsse" />
                <strong>Annahmequote</strong>
              </div>
              <PercentBar items={completedOutcomeItems} />
            </div>
            <div>
              <div className="dashboard-v2-chart-title">
                <MetricLabel label="Quorum über alle Beschlüsse" />
                <strong>Erreicht / nicht erreicht</strong>
              </div>
              <PercentBar items={quorumItems} />
            </div>
          </div>
        </div>

        <div className="panel dashboard-v2-quality-panel">
          <div className="dashboard-v2-panel-head">
            <PanelTitle eyebrow="Vor Beschlussstart" title="Datenqualität" icon="quality" />
          </div>
          {qualityPermissionNotice ? (
            <div
              role="alert"
              style={{
                marginBottom: 10,
                border: "1px solid #fecaca",
                borderRadius: 8,
                background: "#fff5f5",
                color: "#991b1b",
                fontWeight: 700,
                padding: "9px 11px"
              }}
            >
              {qualityPermissionNotice}
            </div>
          ) : null}
          <div className="dashboard-v2-quality-list">
            {summary.dataQuality.map((item) => {
              const isActionable = item.tone === "critical" || item.tone === "warning";
              const rowContent = (
                <>
                  <QualitySymbol tone={item.tone} />
                  <span className="dashboard-v2-quality-main">
                    <strong>{item.label}</strong>
                    <small>{item.count > 0 ? `${numberFormat.format(item.count)} Datensätze prüfen` : "Keine Auffälligkeiten"}</small>
                  </span>
                  <span className={`dashboard-v2-quality-badge dashboard-v2-quality-badge--${item.tone}`}>{qualityToneLabel(item.tone)}</span>
                  {isActionable ? (
                    <span className="dashboard-v2-quality-action" aria-hidden="true">
                      <DashboardIcon name="arrow" />
                    </span>
                  ) : null}
                </>
              );

              if (!isActionable) {
                return (
                  <div key={item.key} className={`dashboard-v2-quality-row dashboard-v2-quality-row--${item.tone}`}>
                    {rowContent}
                  </div>
                );
              }

              return (
                <Link
                  key={item.key}
                  to={qualityTargetHref(item)}
                  className={`dashboard-v2-quality-row dashboard-v2-quality-row--action dashboard-v2-quality-row--${item.tone}`}
                  onClick={(event) => handleQualityClick(event, item)}
                >
                  {rowContent}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="panel dashboard-v2-current-panel">
        <div className="dashboard-v2-panel-head">
          <PanelTitle eyebrow="Aktuell" title="Aktuelle, nicht abgeschlossene Beschlüsse" icon="list" />
        </div>
        {summary.currentResolutions.length === 0 ? (
          <div className="dashboard-v2-empty-state">Keine laufenden oder im Entwurf befindlichen Beschlüsse.</div>
        ) : (
          <>
            {renderCurrentResolutionsTable(displayedCurrentResolutions, "dashboard")}
            {hasMoreCurrentResolutions ? (
              <div className="dashboard-v2-current-more">
                <button type="button" onClick={() => setIsCurrentResolutionsModalOpen(true)}>
                  Weitere Beschlüsse
                </button>
              </div>
            ) : null}
          </>
        )}
        {isCurrentResolutionsModalOpen && typeof document !== "undefined"
          ? createPortal(
              <div
                className="modal-backdrop dashboard-v2-current-modal-backdrop"
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget) {
                    setOpenActionMenuId(null);
                    setIsCurrentResolutionsModalOpen(false);
                  }
                }}
              >
                <section className="panel modal-card dashboard-v2-current-modal-card" role="dialog" aria-modal="true" aria-label="Alle aktuellen Beschlüsse">
                  <div className="dashboard-v2-current-modal-head">
                    <PanelTitle eyebrow="Aktuell" title="Alle aktuellen Beschlüsse" icon="list" />
                    <button
                      type="button"
                      className="dashboard-v2-current-modal-close"
                      aria-label="Fenster schließen"
                      onClick={() => {
                        setOpenActionMenuId(null);
                        setIsCurrentResolutionsModalOpen(false);
                      }}
                    >
                      ×
                    </button>
                  </div>
                  {renderCurrentResolutionsTable(summary.currentResolutions, "modal")}
                </section>
              </div>,
              document.body
            )
          : null}
      </section>
    </div>
  );
};
